/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {getDeviceConnectionState} from '@ion/core/src/services/Connect';
import {DeviceConnectionStateResult} from '@ion/core/src/types/services';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type ConnectionStatusMap = Record<string, boolean>;

export type UseVehicleMqttConnectionStatusResult = UseDefaultQueryResult & {
  connectionStatusMap: ConnectionStatusMap;
  refresh: () => void;
};

const toConnectionStatusMap = (
  results: DeviceConnectionStateResult[]
): ConnectionStatusMap =>
  results.reduce<ConnectionStatusMap>((acc, item) => {
    acc[item.esn] = item.status === 'CONNECTED';
    return acc;
  }, {});

/**
 * Retrieves MQTT connection status for a list of ESNs from the DM gateway.
 * Results are cached and shared between the Vehicle Manager grid and the
 * Vehicle Details dialog.
 *
 * - CONNECTED maps to true; any other status or missing result maps to false.
 * - On request failure all vehicles remain disconnected (retry: false).
 *
 * ```typescript
 * import useVehicleMqttConnectionStatus from
 *   '@ion/query/src/hooks/useVehicleMqttConnectionStatus';
 *
 * const {connectionStatusMap} = useVehicleMqttConnectionStatus(esns);
 * const isConnected = connectionStatusMap[esn] ?? false;
 * ```
 *
 * @param esns Stable array of ESNs for the current grid page (max 100).
 */
const useVehicleMqttConnectionStatus = (
  esns: string[]
): UseVehicleMqttConnectionStatusResult => {
  const queryClient = useQueryClient();

  const key = useMemo(() => (
    createKey(QueryKey.VEHICLE_CONNECTION_STATUS, esns)
  ), [esns]);

  const query = useQuery<ConnectionStatusMap>(key, async() => {
    const response = await getDeviceConnectionState(esns.join(','));
    const results  = response?.response?.results || [];
    return toConnectionStatusMap(results);
  }, {
    staleTime : getStaleTime(1, false, false) as number,
    enabled   : esns.length > 0,
    retry     : false
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    connectionStatusMap : query.data || {},
    refresh
  }) as UseVehicleMqttConnectionStatusResult;
};

export default useVehicleMqttConnectionStatus;
